import { CustomState } from "./CustomState";
import { PaginadoDTO } from "@DTOS";
import { PaginadoEntidadesQueryState, PaginadoEntidadesState } from "./entidades";
import { Nilable } from "@shared/utils";

export abstract class PaginadoState<State, DTO> extends CustomState<State> {
    private entidadesState = new PaginadoEntidadesState<DTO>();
    private entidadesQueryState = new PaginadoEntidadesQueryState<DTO>();
    private condicionesCacheada = true;
    protected condicionesReinicio: Nilable<() => boolean>;

    protected constructor(
        private callbackPedirPagina: (cantidad: number, pagina: number) => Promise<PaginadoDTO<DTO>>,
        private callbackPedirPaginaQuery: (query: string, cantidad: number, pagina: number) => Promise<PaginadoDTO<DTO>>
    ) {
        super();
        if (this.condicionesReinicio != null) {
            this.condicionesCacheada = this.condicionesReinicio();
        }
    }

    /**
     * Devuelve todas las entidades guardadas
     *
     * @protected
     */
    protected obtenerTodos(): DTO[] {
        return this.entidadesState.obtenerEntidades();
    }

    /**
     * Actualiza las entidades guardadas
     *
     * @param callback Callback para modificar las entidades
     * @protected
     */
    protected actualizarEntidades(callback: (dto: DTO) => DTO): DTO[] {
        return this.entidadesState.actualizarEntidades(callback);
    }

    /**
     * Agrega una entidad al estado
     *
     * @param entidad Entidad a agregar
     * @protected
     */
    protected agregarEntidad(entidad: DTO) {
        this.entidadesState.agregarEntidad(entidad);
    }

    /**
     * Devuelve todas las entidades guardadas que corresponden a la ultima query
     *
     * @protected
     */
    protected obtenerTodosQuery(): DTO[] {
        return this.entidadesQueryState.obtenerEntidades();
    }

    /**
     * Devuelve el número total de entidades.
     */
    protected obtenerTotal(): number {
        return this.entidadesState.obtenerTotal();
    }

    /**
     * Devuelve el número total de entidades que corresponden a la ultima query.
     */
    protected obtenerTotalQuery(): number {
        return this.entidadesQueryState.obtenerTotal();
    }

    /**
     * Devuelve la cantidad pedida de entidades
     *
     * @param cantidad Cantidad a devolver
     * @protected
     */
    protected async obtenerCantidad(cantidad: number): Promise<DTO[]> {
        if (this.condicionesReinicio != null && this.condicionesReinicio() !== this.condicionesCacheada) {
            this.entidadesState.reiniciarEntidades();
            this.condicionesCacheada = this.condicionesReinicio();
        }

        if (this.entidadesState.obtenerEntidades().length === 0) {
            const paginado = await this.callbackPedirPagina(cantidad, 1);
            this.entidadesState.cargarPaginado(paginado);
        }

        if (this.entidadesState.obtenerEntidades()!.length < cantidad &&
            this.obtenerPaginaActual(cantidad) < this.entidadesState.obtenerUltimaPagina()) {
            const cantidadFaltante = cantidad - this.entidadesState.obtenerEntidades().length;
            const cantidadTotal = this.entidadesState.obtenerEntidades().length;
            const paginado = await this.callbackPedirPagina(
                cantidadFaltante, Math.ceil(cantidadTotal / cantidadFaltante) + 1
            );
            this.entidadesState.cargarPaginado(paginado);
        }

        return this.entidadesState.obtenerEntidades().slice(0, cantidad);
    }

    /**
     * Devuelve la cantidad pedida de entidades que coincidan con el termino buscado
     *
     * @param query Termino a buscar
     * @param cantidad Cantidad a devolver
     * @protected
     */
    protected async obtenerCantidadQuery(query: string, cantidad: number): Promise<DTO[]> {
        if (this.condicionesReinicio != null && this.condicionesReinicio() !== this.condicionesCacheada) {
            this.entidadesQueryState.reiniciarEntidades();
            this.condicionesCacheada = this.condicionesReinicio();
        }

        if (this.entidadesQueryState.obtenerEntidades().length === 0) {
            const paginado = await this.callbackPedirPaginaQuery(query, cantidad, 1);
            this.entidadesQueryState.cargarPaginadoTermino(query, paginado);
        }

        if (this.entidadesQueryState.obtenerEntidades().length < cantidad &&
            this.obtenerPaginaActual(cantidad) < this.entidadesQueryState.obtenerUltimaPagina()) {
            const cantidadFaltante = cantidad - this.entidadesQueryState.obtenerEntidades().length;
            const paginado = await this.callbackPedirPagina(
                cantidadFaltante, Math.ceil(this.entidadesQueryState.obtenerEntidades().length / cantidadFaltante) + 1
            );
            this.entidadesQueryState.cargarPaginadoTermino(query, paginado);
        }

        return this.entidadesQueryState.obtenerEntidades().slice(0, cantidad);
    }

    /**
     * Obtener la pagina actual
     *
     * @param cantidad Cantidad por pagina
     * @protected
     */
    protected obtenerPaginaActual(cantidad: number): number {
        return Math.ceil(this.entidadesState.obtenerEntidades().length / cantidad);
    }

    /**
     * Obtener la pagina actual
     *
     * @param cantidad Cantidad por pagina
     * @protected
     */
    protected obtenerPaginaActualQuery(cantidad: number): number {
        return Math.ceil(this.entidadesQueryState.obtenerEntidades().length / cantidad);
    }

    /**
     * Cargar las entidades por página y cantidad
     *
     * @param cantidad Cantidad por página
     * @param pagina Página a cargar
     * @protected
     */
    protected async cargarPagina(cantidad: number, pagina: number) {
        if (this.entidadesState.obtenerEntidades().length === 0) {
            const paginado = await this.callbackPedirPagina(cantidad, pagina);
            this.entidadesState.cargarPaginado(paginado);
        }

        if (this.entidadesState.obtenerUltimaPagina() <= pagina) {
            return;
        }

        if (this.entidadesState.obtenerEntidades().length < cantidad * pagina) {
            const paginado = await this.callbackPedirPagina(cantidad, pagina);
            this.entidadesState.cargarPaginado(paginado);
        }
    }

    /**
     * Cargar las entidades por query por página y cantidad
     *
     * @param cantidad Cantidad por página
     * @param pagina Página a cargar
     * @protected
     */
    protected async cargarPaginaQuery(cantidad: number, pagina: number) {
        const query = this.entidadesQueryState.obtenerTermino();
        if (query == null) {
            return;
        }

        if (this.entidadesQueryState.obtenerEntidades().length === 0) {
            const paginado = await this.callbackPedirPaginaQuery(query, cantidad, pagina);
            this.entidadesQueryState.cargarPaginadoTermino(query, paginado);
        }

        if (this.entidadesQueryState.obtenerUltimaPagina() <= pagina) {
            return;
        }

        if (this.entidadesQueryState.obtenerEntidades().length < cantidad * pagina) {
            const paginado = await this.callbackPedirPaginaQuery(query, cantidad, pagina);
            this.entidadesQueryState.cargarPaginadoTermino(query, paginado);
        }
    }

    /**
     * Devuelve la cantidad de entidades por página
     *
     * @param cantidad Cantidad pedida
     * @param pagina Pagina a pedir
     * @protected
     */
    protected async obtenerPorPagina(cantidad: number, pagina: number): Promise<DTO[]> {
        await this.cargarPagina(cantidad, pagina);

        return this.entidadesState.obtenerEntidades().slice((pagina - 1) * cantidad, pagina * cantidad);
    }

    protected limpiarResultadosAnteriores() {
        this.entidadesQueryState.reiniciarEntidades();
    }

    protected limpiarTodo() {
        this.entidadesState.reiniciarEntidades();
        this.entidadesQueryState.reiniciarEntidades();
    }
}
